import React from 'react';
import { Wallet, TrendingUp, Target, Flame, Zap, Trophy, Bell, Search, Menu, ChevronRight, Home, FileText, Users, Settings } from 'lucide-react';

interface MobileDashboardPreviewProps {
  isDark?: boolean;
  className?: string;
}

export function MobileDashboardPreview({ isDark = false, className = '' }: MobileDashboardPreviewProps) {
  const bgColor = isDark ? 'bg-[#0A0A0B]' : 'bg-white';
  const textColor = isDark ? 'text-white' : 'text-slate-900';
  const mutedColor = isDark ? 'text-slate-400' : 'text-slate-500';
  const cardBg = isDark ? 'bg-white/5' : 'bg-slate-50';
  const borderColor = isDark ? 'border-white/10' : 'border-slate-200';

  return (
    <div className={`w-full h-full ${bgColor} ${textColor} ${className} overflow-hidden flex flex-col relative`}>
      {/* Mobile Status Bar */}
      <div className={`px-6 py-3 flex justify-between items-center text-[10px] font-bold tracking-tight border-b ${borderColor}`}>
        <span>9:41</span>
        <div className="flex items-center gap-1.5">
          <div className="w-4 h-2.5 border border-current rounded-[2px] opacity-50 relative">
            <div className="absolute inset-0.5 bg-current rounded-[1px]"></div>
          </div>
          <div className="w-1 h-1 bg-current rounded-full opacity-50"></div>
          <div className="w-1.5 h-1.5 bg-current rounded-full"></div>
        </div>
      </div>

      {/* Mobile Header */}
      <div className={`px-4 py-4 flex items-center justify-between border-b ${borderColor} bg-white/5 backdrop-blur-md`}>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-600/20">
            <Wallet className="w-4 h-4 text-white" />
          </div>
          <span className="font-bold tracking-tight">KwachaLite</span>
        </div>
        
        <div className="flex items-center gap-1">
          <button className="p-2 rounded-xl hover:bg-white/10 transition-colors">
            <Bell className="w-5 h-5" />
          </button>
          <button className="p-2 rounded-xl hover:bg-white/10 transition-colors">
            <Menu className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Mobile Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6 pb-24 custom-scrollbar">
        {/* Balance Card */}
        <div className={`${cardBg} border ${borderColor} rounded-2xl p-5 relative overflow-hidden group`}>
          <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-blue-500/20 to-transparent rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700"></div>
          <div className="relative z-10 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">Net Balance</span>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-lg bg-green-500/10 text-green-500">+12.5%</span>
            </div>
            <p className="text-3xl font-bold tracking-tight">K 245,890</p>
            <div className="w-full bg-white/5 h-1.5 rounded-full overflow-hidden">
              <div className="bg-blue-500 h-full w-[65%]" />
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 gap-4">
          <div className={`${cardBg} border ${borderColor} rounded-2xl p-4 space-y-2`}>
            <div className="flex items-center gap-2 mb-1">
              <div className="p-1.5 bg-green-500/10 rounded-lg text-green-500">
                <TrendingUp className="w-3.5 h-3.5" />
              </div>
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Income</span>
            </div>
            <p className="text-lg font-bold">K 485K</p>
          </div>

          <div className={`${cardBg} border ${borderColor} rounded-2xl p-4 space-y-2`}>
            <div className="flex items-center gap-2 mb-1">
              <div className="p-1.5 bg-red-500/10 rounded-lg text-red-500">
                <TrendingUp className="w-3.5 h-3.5 rotate-180" />
              </div>
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Expenses</span>
            </div>
            <p className="text-lg font-bold">K 239K</p>
          </div>
        </div>

        {/* Recent Transactions */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-bold tracking-tight">Activity</h3>
            <button className="text-[10px] font-bold text-blue-500 uppercase tracking-widest">See All</button>
          </div>
          <div className="space-y-3">
            {[
              { name: 'Shoprite', category: 'Groceries', amount: -45800, icon: '🛒' },
              { name: 'Salary', category: 'Income', amount: 485000, icon: '💰' },
              { name: 'Uber', category: 'Transport', amount: -12500, icon: '🚗' },
            ].map((tx, i) => (
              <div key={i} className={`${cardBg} border ${borderColor} p-3 rounded-xl flex items-center justify-between transition-transform active:scale-95`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-lg">{tx.icon}</div>
                  <div>
                    <p className="text-sm font-bold">{tx.name}</p>
                    <p className={`text-[10px] ${mutedColor}`}>{tx.category}</p>
                  </div>
                </div>
                <span className={`text-sm font-bold ${tx.amount > 0 ? 'text-green-500' : ''}`}>
                  {tx.amount > 0 ? '+' : ''}K {Math.abs(tx.amount).toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-4">
          <button className="h-12 bg-blue-600 text-white rounded-xl font-bold text-sm shadow-lg shadow-blue-600/20 active:scale-95 transition-transform flex items-center justify-center gap-2">
            <Zap className="w-4 h-4 fill-current" />
            Add
          </button>
          <button className={`${cardBg} border ${borderColor} h-12 rounded-xl font-bold text-sm active:scale-95 transition-transform`}>
            Stats
          </button>
        </div>
      </div>

      {/* Mobile Bottom Navigation */}
      <div className={`${cardBg} border-t ${borderColor} px-6 py-4 flex justify-between items-center absolute bottom-0 left-0 right-0 bg-white/10 backdrop-blur-xl`}>
        <button className="p-2 text-blue-500"><Home className="w-6 h-6" /></button>
        <button className="p-2 text-slate-500 opacity-50"><FileText className="w-6 h-6" /></button>
        <button className="p-2 text-slate-500 opacity-50"><Target className="w-6 h-6" /></button>
        <button className="p-2 text-slate-500 opacity-50"><Users className="w-6 h-6" /></button>
        <button className="p-2 text-slate-500 opacity-50"><Settings className="w-6 h-6" /></button>
      </div>
    </div>
  );
}